import React, { useEffect, useState } from "react";
import { FormControl, FormLabel, Typography } from "@mui/material";
import { ANDatePicker } from "@answersnow/pkg-theme";

import ConfirmDialog from "../../shared/ConfirmDialog";
import type { RecordNewSessionDialogProps } from "./RecordNewSessionDialog.types";

/**
 * Dialog for starting an ad hoc Data Sheets session — one not tied to a
 * therapy appointment (e.g. logging reported behavior from over the
 * weekend). Opened from Session History's "Record New Session" button.
 *
 * The date defaults to today and cannot be set in the future; the confirm
 * action is disabled until a date is chosen. Confirming hands the date to
 * the caller, which routes to Data Sheets with client + date context and no
 * appointmentId.
 *
 * @param props
 * @param props.open      - Controls dialog visibility
 * @param props.onClose   - Dismiss handler (no save)
 * @param props.onSubmit  - Called with the selected date on confirm
 * @param props.isLoading - Disables actions while navigation/creation is in flight
 */
const RecordNewSessionDialog: React.FC<RecordNewSessionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [date, setDate] = useState<Date | null>(null);

  // Default to today every time the dialog opens.
  useEffect(() => {
    if (open) setDate(new Date());
  }, [open]);

  const handleConfirm = () => {
    if (date) onSubmit(date);
  };

  return (
    <ConfirmDialog
      open={open}
      title="Record New Session"
      confirmLabel="Set Session Date And Continue"
      onClose={onClose}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      confirmDisabled={!date}
      maxWidth="xs"
    >
      <FormControl fullWidth>
        <FormLabel
          sx={{ typography: "caption", fontWeight: 500, color: "text.secondary", mb: 1 }}
        >
          Session Date
        </FormLabel>
        <ANDatePicker
          id="recordNewSessionDatePicker"
          value={date}
          onChange={(value: Date | null) => setDate(value)}
          format="MM/dd/yyyy"
          disableFuture
          slotProps={{
            textField: { fullWidth: true, size: "small" },
            // ANDatePicker defaults the calendar popper to disablePortal,
            // which gets clipped by this dialog's scrollable DialogContent
            // (only the month header renders, no day grid). Portal it to
            // the body instead so it isn't bounded by the dialog.
            popper: { disablePortal: false },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Provide a session date for this data sheet
        </Typography>
      </FormControl>
    </ConfirmDialog>
  );
};

export default RecordNewSessionDialog;

export * from "./RecordNewSessionDialog.types";
